import React from 'react'

import { SearchMode, SearchUrl, SuggestionType } from 'src/types/search'
import { useDevice } from 'src/hooks/useDevice'
import { BreakpointSizes } from 'src/types/device'
import { getPaxesfromUrlParams } from 'client/utils'
import { setSearchParamsLocalStorage } from 'lib/storage'
import { useSearch } from 'src/hooks/useSearch'
import { OmniSearch } from './Omnisearch'
import { OmnisearchBtn } from './OmnisearchBtn'

import styles from './styles.module.css'
import { useSuggesterSearch } from 'src/hooks/useSuggesterSearch'
import { OmnisearchMobile } from './OmnisearchMobile'

enum Views {
  Desktop = 'desktop',
  Tablet = 'tablet',
  Mobile = 'mobile',
}

interface ISearchProps {
  mode: SearchMode
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void
  onModeChange: (visualMode: SearchMode) => void
  className?: string
  searchUrl: SearchUrl
}

export interface ISearch {
  adults: string
  children: string
  country: string
  type: string
  slug: string
  text: string
  description: string
  citySlug: string
  checkin: string
  checkout: string
}

const getView = ({ activeBreakpoint }: { activeBreakpoint: number }) => {
  if (activeBreakpoint >= BreakpointSizes.L) {
    return Views.Desktop
  }

  if (activeBreakpoint >= BreakpointSizes.M) {
    return Views.Tablet
  }
  return Views.Mobile
}

const componentMap = {
  [Views.Desktop]: ({ onClick }: Pick<ISearchProps, 'onClick'>) => (
    <OmniSearch mode={SearchMode.Borderless} onClick={onClick} />
  ),
  [Views.Tablet]: ({ onClick }: Pick<ISearchProps, 'onClick'>) => (
    <OmnisearchBtn onClick={onClick} />
  ),
  [Views.Mobile]: ({ onClick, searchUrl }: Pick<ISearchProps, 'onClick' | 'searchUrl'>) => (
    <OmnisearchMobile searchUrl={searchUrl} onClick={onClick} />
  ),
}

type ISearchWithBoxShape = ISearch &
  SuggestionType & {
    BoxShape?: { TopLeft: { Lat: number; Lon: number }; BottomRight: { Lat: number; Lon: number } }
  }
export function Search() {
  const { oldSearchUrl, draftLocation } = useSearch()
  const { addUserLastSearch } = useSuggesterSearch()
  const { href } = oldSearchUrl
  const { activeBreakpoint } = useDevice()

  const search = {
    ...href.query,
    ...draftLocation,
  } as ISearchWithBoxShape

  const saveSearch = (query: ISearchWithBoxShape) => {
    const paxes = getPaxesfromUrlParams({
      adults: href.query.adults,
      children: href.query.children,
    }) as { adults: number[]; children: number[][] }

    if (!query?.ID) return

    addUserLastSearch({
      location: {
        id: query.ID,
        country: query.Country,
        type: query.Type,
        slug: query.slug,
        text: query.Text,
        description: query.Description,
        citySlug: query.citySlug,
        coordinates: {
          lat: query.Coordinate.lat,
          lon: query.Coordinate.lon,
        },
        ...(query.BoxShape && {
          boxShape: {
            topLeft: {
              lat: query.BoxShape?.TopLeft?.Lat ?? query.BoxShape?.topLeft?.lat,
              lon: query.BoxShape?.TopLeft?.Lon ?? query.BoxShape?.topLeft?.lon,
            },
            bottomRight: {
              lat: query.BoxShape?.BottomRight?.Lat ?? query.BoxShape?.bottomRight?.lat,
              lon: query.BoxShape?.BottomRight?.Lon ?? query.BoxShape?.bottomRight?.lon,
            },
          },
        }),
      },
      dates: {
        ci: query.checkin,
        co: query.checkout,
      },
      paxes,
    })
  }

  const handleSearch = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation()

    const paxes = getPaxesfromUrlParams({
      adults: href.query.adults,
      children: href.query.children,
    })

    setSearchParamsLocalStorage({
      checkIn: href.query.checkin,
      checkOut: href.query.checkout,
      adults: paxes.adults,
      children: paxes.children,
    })

    saveSearch(search)
  }

  const view = getView({ activeBreakpoint })
  const Component = componentMap[view]

  return (
    <div data-testid="search-wrapper" className={styles.content}>
      <Component onClick={handleSearch} searchUrl={oldSearchUrl} />
    </div>
  )
}
